/**
 * Site Refresh 2026: blog article page (A23). Markup: snippets/refresh-article.liquid.
 * The table of contents, heading ids and anchors are server-rendered; this file only:
 * - Share: Web Share API, else copy the link and say "Link copied!"
 * - highlights the current section in the TOC (aria-current="location")
 * - folds each H2's sub-sections behind a chevron; the H2 the reader is in opens (A28)
 * - phone/tablet: shows the floating "Table of Contents" pill once the reader is past the
 *   <details>, and opens the list in a modal <dialog> (Esc closes, focus returns)
 * - makes body tables that scroll sideways keyboard-focusable
 */
(function () {
	'use strict';

	var root = document.querySelector('[data-rf-article]');
	if (!root) return;

	var status = root.querySelector('[data-rfa-status]');

	/* ---------- Share ---------- */
	function announce(text) {
		if (!status) return;
		status.textContent = '';
		window.setTimeout(function () { status.textContent = text; }, 50);
	}

	function copyText(text) {
		if (navigator.clipboard && window.isSecureContext) {
			return navigator.clipboard.writeText(text);
		}
		return new Promise(function (resolve, reject) {
			var input = document.createElement('textarea');
			input.value = text;
			input.setAttribute('readonly', '');
			input.style.position = 'fixed';
			input.style.opacity = '0';
			document.body.appendChild(input);
			input.select();
			var ok = false;
			try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
			document.body.removeChild(input);
			if (ok) { resolve(); } else { reject(new Error('copy failed')); }
		});
	}

	root.querySelectorAll('[data-rfa-share]').forEach(function (button) {
		button.addEventListener('click', function () {
			var url = button.getAttribute('data-url') || window.location.href;
			var title = button.getAttribute('data-title') || document.title;
			var copied = button.getAttribute('data-copied') || '';
			var label = button.querySelector('.rfa-share__text');

			var fallback = function () {
				copyText(url).then(function () {
					announce(copied);
					if (label && !button.hasAttribute('data-rfa-busy')) {
						var original = label.textContent;
						button.setAttribute('data-rfa-busy', '');
						label.textContent = copied;
						window.setTimeout(function () {
							label.textContent = original;
							button.removeAttribute('data-rfa-busy');
						}, 2000);
					}
				}).catch(function () {});
			};

			if (navigator.share) {
				navigator.share({ title: title, url: url }).catch(function (error) {
					if (!error || error.name !== 'AbortError') fallback();
				});
			} else {
				fallback();
			}
		});
	});

	/* ---------- Current section in the TOC ---------- */
	var tocLinks = Array.prototype.slice.call(root.querySelectorAll('[data-rfa-toc] a[href^="#"]'));
	var headings = [];
	var seen = {};
	tocLinks.forEach(function (link) {
		var id = decodeURIComponent(link.getAttribute('href').slice(1));
		if (seen[id]) return;
		var el = document.getElementById(id);
		if (el) {
			seen[id] = true;
			headings.push(el);
		}
	});

	var currentId = null;
	var aside = root.querySelector('.rfa-aside');

	function setCurrent(id) {
		if (id === currentId) return;
		currentId = id;
		followSection(id);
		root.querySelectorAll('[data-rfa-toc] a[href^="#"]').forEach(function (link) {
			if (id && link.getAttribute('href') === '#' + id) {
				link.setAttribute('aria-current', 'location');
				/* keep the active item visible inside the sticky sidebar, without moving the page */
				if (aside && aside.contains(link) && aside.scrollHeight > aside.clientHeight) {
					var top = link.getBoundingClientRect().top - aside.getBoundingClientRect().top + aside.scrollTop;
					if (top < aside.scrollTop || top > aside.scrollTop + aside.clientHeight - 48) {
						aside.scrollTop = Math.max(0, top - aside.clientHeight / 3);
					}
				}
			} else {
				link.removeAttribute('aria-current');
			}
		});
	}

	var ticking = false;
	function update() {
		ticking = false;
		var line = window.innerHeight * 0.3;
		var active = null;
		for (var i = 0; i < headings.length; i++) {
			if (headings[i].getBoundingClientRect().top <= line) {
				active = headings[i];
			} else {
				break;
			}
		}
		setCurrent(active ? active.id : null);
		updatePill();
	}

	function requestUpdate() {
		if (!ticking) {
			ticking = true;
			window.requestAnimationFrame(update);
		}
	}

	/* ---------- Floating pill + dialog (phone and tablet) ---------- */
	var pill = root.querySelector('[data-rfa-pill]');
	var dialog = root.querySelector('[data-rfa-dialog]');
	var details = root.querySelector('[data-rfa-toc-details]');
	var body = root.querySelector('[data-rfa-body]');
	var dialogList = dialog ? dialog.querySelector('[data-rfa-dialog-list]') : null;
	var sourceList = details ? details.querySelector('.rfa-toc__list') : null;
	var canDialog = !!(dialog && typeof dialog.showModal === 'function');

	if (dialogList && sourceList && !dialogList.children.length) {
		dialogList.innerHTML = sourceList.innerHTML;
	}

	/* ---------- Folding sub-sections (A28) ----------
	 * H3s stay in the HTML; once this runs, each H2 with sub-sections gets a chevron button
	 * and its list folds. The H2 the reader is in opens by itself ("auto") and closes when they
	 * move on; one opened by hand ("hand") stays open until they close it. */
	var showLabel = root.getAttribute('data-toc-show') || '';
	var hideLabel = root.getAttribute('data-toc-hide') || '';
	var chevron = '<svg aria-hidden="true" focusable="false" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M9 6l6 6-6 6"/></svg>';
	var groups = [];

	function setOpen(group, open, by) {
		group.items.forEach(function (entry) {
			entry.item.classList.toggle('rfa-toc__item--open', open);
			entry.button.setAttribute('aria-expanded', open ? 'true' : 'false');
			entry.button.setAttribute('aria-label', (open ? hideLabel : showLabel).replace('%T%', group.title));
		});
		group.openBy = open ? by : null;
	}

	root.querySelectorAll('[data-rfa-toc]').forEach(function (list, listIndex) {
		var index = 0;
		Array.prototype.forEach.call(list.children, function (item) {
			if (!item.classList.contains('rfa-toc__item--h2')) return;
			var link = item.querySelector('.rfa-toc__link');
			var sub = item.querySelector('.rfa-toc__sub');
			var i = index++;
			if (!link || !sub) return;
			if (!groups[i]) {
				groups[i] = { title: link.textContent.trim(), ids: {}, items: [], openBy: null };
				sub.querySelectorAll('a[href^="#"]').forEach(function (a) {
					groups[i].ids[decodeURIComponent(a.getAttribute('href').slice(1))] = true;
				});
			}
			var group = groups[i];
			sub.id = 'rfa-toc-sub-' + listIndex + '-' + i;
			var button = document.createElement('button');
			button.type = 'button';
			button.className = 'rfa-toc__toggle';
			button.setAttribute('aria-controls', sub.id);
			button.innerHTML = chevron;
			item.insertBefore(button, sub);
			group.items.push({ item: item, button: button });
			button.addEventListener('click', function () {
				var open = button.getAttribute('aria-expanded') !== 'true';
				setOpen(group, open, 'hand');
			});
		});
		list.classList.add('rfa-toc--js');
	});
	groups = groups.filter(Boolean);
	groups.forEach(function (group) { setOpen(group, false); });

	/* open the H2 that holds the current section (an H3 id, or the H2's own link) */
	function followSection(id) {
		groups.forEach(function (group) {
			var holds = false;
			if (id) {
				holds = !!group.ids[id] || group.items.some(function (entry) {
					var link = entry.item.querySelector('.rfa-toc__link');
					return link && link.getAttribute('href') === '#' + id;
				});
			}
			if (holds && !group.current && !group.openBy) {
				setOpen(group, true, 'auto');
			} else if (!holds && group.openBy === 'auto') {
				setOpen(group, false);
			}
			group.current = holds;
		});
	}

	if (window.location.hash) {
		try { followSection(decodeURIComponent(window.location.hash.slice(1))); } catch (e) {}
	}

	function updatePill() {
		if (!pill || !details || !body || !canDialog) return;
		var pastToc = details.getBoundingClientRect().bottom < 0;
		var inBody = body.getBoundingClientRect().bottom > window.innerHeight * 0.6;
		var show = pastToc && inBody && window.getComputedStyle(details).display !== 'none';
		if (show === pill.hidden) {
			if (!show && document.activeElement === pill) return;
			pill.hidden = !show;
		}
	}

	if (pill && canDialog) {
		pill.addEventListener('click', function () {
			pill.setAttribute('aria-expanded', 'true');
			dialog.showModal();
			var current = dialog.querySelector('[aria-current="location"]') || dialog.querySelector('a');
			if (current) current.focus();
		});
		var followLink = null;
		dialog.addEventListener('close', function () {
			pill.setAttribute('aria-expanded', 'false');
			if (followLink) {
				/* a section was chosen: move focus to its heading so reading starts there */
				var target = document.getElementById(followLink);
				followLink = null;
				if (target) {
					if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
					target.focus({ preventScroll: true });
				}
			} else if (!pill.hidden) {
				pill.focus();
			}
		});
		dialog.addEventListener('click', function (event) {
			if (event.target === dialog) {
				dialog.close();
				return;
			}
			var link = event.target.closest ? event.target.closest('a[href^="#"]') : null;
			if (link) {
				followLink = decodeURIComponent(link.getAttribute('href').slice(1));
				dialog.close();
			}
		});
		var closeButton = dialog.querySelector('[data-rfa-dialog-close]');
		if (closeButton) {
			closeButton.addEventListener('click', function () { dialog.close(); });
		}
	}

	if (headings.length || pill) {
		window.addEventListener('scroll', requestUpdate, { passive: true });
		window.addEventListener('resize', requestUpdate);
		requestUpdate();
	}

	/* ---------- Wide tables: focusable scroll area ---------- */
	function markTables() {
		root.querySelectorAll('.rfa-prose table').forEach(function (table) {
			if (table.scrollWidth > table.clientWidth + 1) {
				table.setAttribute('tabindex', '0');
			} else {
				table.removeAttribute('tabindex');
			}
		});
	}
	markTables();
	window.addEventListener('resize', function () { window.requestAnimationFrame(markTables); });
})();
